import { useMemo, useState, useEffect } from 'react';
import { useField, useForm } from '@shopify/react-form';
import { RestrictionType } from '@sdk/messages/types/RestrictionType';
import { Attachment } from '@sdk/attachments/types';
import gettext from '@sdk/lang/gettext';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';
import useToast from '@sdk/common/toast/useToast';
import { PersonaPickerOption } from '@sdk/components/forms/personaPickers/PersonaPicker/personaPicker.types';
import { createPersonaPickerRequiredValidator } from '@sdk/components/forms/personaPickers/utils/createPersonaPickerRequiredValidator';
import { usePersonaCollection } from '@sdk/personaCollection/PersonaCollectionContext';

import {
  useConversationCreateRestrictions,
  useRestrictedToUsersField,
} from '@web/conversations/ComposeConversation/composeConversation.hooks';
import { FormSubmitStatuses } from '@web/forms/types';
import { getCreateGroupConversationPayload } from '@web/conversations/CreateGroupConversation/createGroupConversation.utils';
import { useWorkingByDayPersonaOption } from '@web/common/hooks/useWorkingByDayPersonaOption/useWorkingByDayPersonaOption';
import { usePostBulkMessage } from '@web/conversations/hooks/usePostBulkMessage';
import { useCreateConversation } from '@web/conversations/hooks/useCreateConversation';
import { useNavigate } from '@web/Router/router.hooks';

export const useCreateGroupConversation = (bulkMessageEnabled = false) => {
  const navigate = useNavigate();
  const {
    possiblyUnwrapOption,
    workingTodayGroup,
    workingTodayGroupId,
    workingTomorrowGroup,
    workingTomorrowGroupId,
  } = useWorkingByDayPersonaOption();
  const formFields = useCreateGroupConversationFormFields(
    [workingTodayGroupId, workingTomorrowGroupId],
    bulkMessageEnabled
  );
  const { showErrorToast } = useToast();
  const { mutateAsync: postBulkMessages } = usePostBulkMessage();
  const { mutateAsync: createConversation } = useCreateConversation();
  const [isNavigatingAway, setIsNavigatingAway] = useState(false);

  const {
    dirty: isDirty,
    fields,
    reset,
    validate,
    submit,
    submitErrors,
    submitting,
  } = useForm({
    fields: formFields,
    onSubmit: async (fieldValues) => {
      try {
        if (bulkMessageEnabled) {
          const privateConversations = await postBulkMessages({
            attachments: fieldValues.attachments,
            content: fieldValues.content,
            personas: fieldValues.members
              .flatMap(possiblyUnwrapOption)
              .map((member) => ({ id: member.id })),
          });

          navigate.toConversationDetails({
            params: {
              id: privateConversations[0].id,
              slug: privateConversations[0].slug,
            },
          });
        } else {
          const addPayload = getCreateGroupConversationPayload(fieldValues, possiblyUnwrapOption);
          const conversation = await createConversation(addPayload);

          setIsNavigatingAway(true);
          navigate.toConversationDetails({
            params: {
              id: conversation.id,
              slug: conversation.slug,
            },
          });
        }
        return { status: FormSubmitStatuses.success };
      } catch (e) {
        const errorMsg = getErrorForToast(e);
        showErrorToast(errorMsg);
        return { status: FormSubmitStatuses.fail, errors: [{ message: errorMsg }] };
      }
    },
  });

  const isFormValid = useMemo(() => {
    const result = validate();

    return !result.length;
    // @TODO rewrite using react-hook-form, shopify lib does not support onChange validation,
    // hence such workaround has been used
    // eslint-disable-next-line
  }, [
    fields.content.value,
    fields.name.value,
    fields.members.value,
    fields.restricted.value,
    fields.restrictedToUsers.value,
    validate,
  ]);

  const cancel = () => {
    reset();
    navigate.toConversationCompose();
  };

  return {
    cancel,
    everyoneWorkingByDayOptions: [workingTodayGroup, workingTomorrowGroup].filter(Boolean),
    fields,
    isDirty,
    isFormValid,
    submit,
    submitErrors,
    submitting,
    isNavigatingAway,
  };
};

function useCreateGroupConversationFormFields(
  alwaysValidPickerIds: ID[],
  isContentRequired = false
) {
  const personaCollection = usePersonaCollection();

  const name = useField({
    value: '',
    validates: [],
  });

  // @INFO workingToday and workingTomorrow groups are shown only if not empty, hence they are always valid
  const pickerValidator = createPersonaPickerRequiredValidator(
    personaCollection,
    alwaysValidPickerIds
  );

  const members = useField<PersonaPickerOption[]>({
    value: [],
    validates: [pickerValidator],
  });

  const restricted = useField({
    value: RestrictionType.NONE,
    validates: [],
  });

  const attachments = useField<Attachment[]>([]);

  const content = useField<string>(
    {
      value: '',
      validates: (value: string) => {
        if (isContentRequired && !value.trim().length) {
          return gettext('Content field is required');
        }
      },
    },
    [isContentRequired]
  );

  const restrictedToUsersFields = useRestrictedToUsersField(restricted.value, []);

  return {
    attachments,
    content,
    members,
    name,
    restricted,
    restrictedToUsers: restrictedToUsersFields,
  };
}

export function useCreateGroupConversationRestrictions() {
  const navigate = useNavigate();
  const { canStartGroupConversation } = useConversationCreateRestrictions();

  useEffect(() => {
    if (!canStartGroupConversation) {
      navigate.toConversationRoot();
    }
  }, [navigate, canStartGroupConversation]);
}
